import { ClipboardList, ScanSearch, MessageCircle, PenLine, Wallet, MapPin, Timer, Hash } from 'lucide-react'
import { useScrollReveal, useStaggerReveal } from '../../hooks/useScrollReveal.js'
import { useParallax } from '../../hooks/useParallax.js'

/* ─── Data ──────────────────────────────────────────────────── */
const STEPS = [
  {
    number: '01',
    icon:   ClipboardList,
    title:  'Send a Request',
    body:
      'Tell us what you need, your budget, and where you are. ' +
      'Pick how fast you need it: Now, Today, or Flexible. ' +
      'Takes less than a minute.',
  },
  {
    number: '02',
    icon:   ScanSearch,
    title:  'We find the right providers',
    body:
      'DecidA checks your request and sends it to 3–5 verified ' +
      'providers in your area who can actually do the job. ' +
      'No random lists. Only matched people.',
  },
  {
    number: '03',
    icon:   MessageCircle,
    title:  'Receive offers and choose',
    body:
      'Providers see your request in the app and send their offers. ' +
      'You receive all offers in the app, compare them, and pick the one you want.',
  },
]

const REQUEST_FIELDS = [
  { icon: PenLine, label: 'What do you need?' },
  { icon: Wallet,  label: 'Your budget'       },
  { icon: MapPin,  label: 'Your area'         },
  { icon: Timer,   label: 'How urgent?'       },
  { icon: Hash,    label: 'Quantity'          },
]

/* ─── Step card ─────────────────────────────────────────────── */
function StepCard({ number, icon: Icon, title, body, isLast, delay }) {
  return (
    <div className={`relative reveal reveal-delay-${delay}
                     flex flex-row gap-4 items-start
                     md:flex-col md:gap-0`}>
      {!isLast && (
        <div
          aria-hidden="true"
          className="hidden md:block absolute top-7 left-[calc(50%+28px)] right-0
                     h-px bg-gradient-to-r from-primary/20 to-transparent"
        />
      )}
      {/* Icon — compact on mobile, larger on desktop */}
      <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-primary-light
                      flex items-center justify-center flex-shrink-0
                      md:mb-5 shadow-sm transition-transform duration-300 hover:scale-110">
        <Icon className="w-5 h-5 md:w-6 md:h-6 text-primary" aria-hidden="true" />
      </div>
      {/* Text */}
      <div className="flex-1">
        <p className="eyebrow mb-1 md:mb-2">Step {number}</p>
        <h3 className="text-[15px] md:text-[17px] font-bold text-brand-dark mb-1.5 md:mb-2">{title}</h3>
        <p className="text-sm text-brand-gray leading-relaxed">{body}</p>
      </div>
    </div>
  )
}

/* ─── Section ───────────────────────────────────────────────── */
export default function HowItWorks() {
  const headerRef  = useScrollReveal()
  const stepsRef   = useStaggerReveal()
  const missionRef = useScrollReveal()
  const bgOrbRef   = useParallax({ speed: 0.3 })

  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Parallax ambient orb */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          ref={bgOrbRef}
          className="parallax-layer absolute -top-20 -left-20 w-[400px] h-[400px]
                     rounded-full bg-primary/5 blur-[80px]"
        />
      </div>
      <div className="section-container relative z-10">

        {/* Header */}
        <div ref={headerRef} className="reveal max-w-xl mb-10 md:mb-16">
          <p className="eyebrow mb-3">
            <span className="w-5 h-px bg-primary" />
            Simple process
          </p>
          <h2
            className="font-extrabold text-brand-dark mb-4"
            style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.75rem)' }}
          >
            How It Works
          </h2>
          <p className="text-base text-brand-gray leading-relaxed">
            One quick request replaces hours of asking around.
            DecidA brings the right providers straight to you.
          </p>
        </div>

        {/* Steps — stagger triggered when grid enters viewport */}
        <div
          ref={stepsRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-10 md:mb-14"
        >
          {STEPS.map((step, i) => (
            <StepCard
              key={step.number}
              {...step}
              isLast={i === STEPS.length - 1}
              delay={i + 1}
            />
          ))}
        </div>

        {/* Mission anatomy */}
        <div
          ref={missionRef}
          className="reveal rounded-2xl bg-primary-light border border-primary/10 p-8
                     transition-shadow duration-300 hover:shadow-card-md"
        >
          <p className="eyebrow mb-5">
            <span className="w-5 h-px bg-primary" />
            What goes into a Request
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap gap-3">
            {REQUEST_FIELDS.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="flex items-center justify-center gap-2 text-sm font-medium
                           text-brand-dark bg-white border border-primary/10
                           rounded-xl px-4 py-3 shadow-sm
                           transition-all duration-200 hover:border-primary/30 hover:shadow-md"
              >
                <Icon className="w-4 h-4 text-primary flex-shrink-0" aria-hidden="true" />
                {label}
              </span>
            ))}
          </div>
          <p className="mt-4 text-xs text-brand-gray">
            Add a short note if it helps: the make of your phone, a preferred location,
            or any detail that helps providers give you a better offer.
          </p>
        </div>

      </div>
    </section>
  )
}
